import { useState, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Lock } from 'lucide-react';
import { toast } from 'sonner';
import { motion } from 'framer-motion';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminLogin = () => {
  const navigate = useNavigate();
  const [pin, setPin] = useState(['', '', '', '']);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const inputRefs = [useRef(), useRef(), useRef(), useRef()];

  const handleChange = (index, value) => {
    if (!/^\d*$/.test(value)) return;
    const newPin = [...pin];
    newPin[index] = value.slice(-1);
    setPin(newPin);
    setError('');

    if (value && index < 3) {
      inputRefs[index + 1].current?.focus();
    }

    if (newPin.every((d) => d !== '') && index === 3) {
      handleLogin(newPin.join(''));
    }
  };

  const handleKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !pin[index] && index > 0) {
      inputRefs[index - 1].current?.focus();
    }
  };

  const handleLogin = async (code) => {
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/admin/login`, { pin: code });
      localStorage.setItem('admin_token', data.token);
      toast.success('Welcome back!');
      navigate('/admin/dashboard');
    } catch {
      setError('Invalid PIN code');
      setPin(['', '', '', '']);
      inputRefs[0].current?.focus();
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-4" data-testid="admin-login-page">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="w-full max-w-sm"
      >
        <div className="bg-white rounded-[2.5rem] shadow-elevated border border-border/40 p-8 space-y-8">
          <div className="text-center space-y-3">
            <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mx-auto">
              <Lock className="w-8 h-8 text-primary" />
            </div>
            <h1 className="font-fredoka font-bold text-2xl text-foreground" data-testid="admin-login-heading">
              Admin Access
            </h1>
            <p className="text-sm text-muted-foreground">Enter your 4-digit PIN</p>
          </div>

          <div className="flex justify-center gap-4">
            {pin.map((digit, i) => (
              <input
                key={i}
                ref={inputRefs[i]}
                type="password"
                inputMode="numeric"
                maxLength={1}
                value={digit}
                onChange={(e) => handleChange(i, e.target.value)}
                onKeyDown={(e) => handleKeyDown(i, e)}
                disabled={loading}
                className="w-14 h-16 text-center text-2xl font-bold rounded-2xl border-2 border-border/60 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20 transition-all bg-muted/30"
                data-testid={`admin-pin-input-${i}`}
              />
            ))}
          </div>

          {error && (
            <motion.p
              initial={{ opacity: 0, y: -5 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-center text-destructive text-sm font-semibold"
              data-testid="admin-login-error"
            >
              {error}
            </motion.p>
          )}

          {loading && (
            <p className="text-center text-muted-foreground text-sm">Verifying...</p>
          )}
        </div>

        <div className="text-center mt-6">
          <Link to="/" className="text-sm text-muted-foreground hover:text-primary transition-colors">
            Back to website
          </Link>
        </div>
      </motion.div>
    </div>
  );
};

export default AdminLogin;
